/**
 * Authentication hook
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { User } from 'firebase/auth';
import {
  subscribeToAuthState,
  signInWithGoogle,
  signInWithEmailPassword,
  signUpWithEmailPassword,
  sendPasswordReset,
  resendVerificationEmail,
  refreshEmailVerified,
  signOutUser,
  getCurrentUserToken,
  auth,
} from '../services/firebase';
import { getApiBaseUrl } from '../utils/apiConfig';
import { resetUserScopedClientState, LAST_AUTH_UID_KEY } from '../utils/sessionReset';
import { frontDoorOn } from '../lib/frontDoor/flag';
import { clearDraft } from '../lib/frontDoor/draft';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  isApproved: boolean;
  isAdmin: boolean;
  emailVerified: boolean;
  waitlistPosition?: number;
}

/** How long to wait before each new ask when the approval check got no answer. */
const APPROVAL_RETRY_MS = [1000, 2000, 4000];

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
    isApproved: false,
    isAdmin: false,
    emailVerified: false,
  });

  // Register the user with the backend, then read approval status. Runs for any
  // Firebase user (Google or email/password). The backend fires the welcome
  // email here once the token reports email_verified=true.
  // Only an answer decides approval: approved=false, or a 403. A check that never got one (a navigation cut the fetch
  // off, a network blip, a 5xx) leaves it undecided and asks again, so an approved user is never sent to the waitlist
  // because a page loaded mid-check. After the last retry it gives up as before.
  const retry = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const syncApproval = useCallback(async (user: User, attempt = 0): Promise<void> => {
    clearTimeout(retry.current);
    const undecided = (error: string | null) => {
      if (attempt < APPROVAL_RETRY_MS.length) {
        retry.current = setTimeout(() => void syncApproval(user, attempt + 1), APPROVAL_RETRY_MS[attempt]);
        return;
      }
      setAuthState(prev => ({
        ...prev,
        user,
        loading: false,
        emailVerified: !!user.emailVerified,
        isApproved: false,
        isAdmin: false,
        ...(error ? { error } : {}),
      }));
    };
    try {
      const token = await user.getIdToken();
      const apiBaseUrl = getApiBaseUrl();

      await fetch(`${apiBaseUrl}/approval/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const response = await fetch(`${apiBaseUrl}/approval/status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setAuthState(prev => ({
          ...prev,
          user,
          loading: false,
          emailVerified: !!user.emailVerified,
          isApproved: data.approved === true,
          isAdmin: data.admin === true,
          waitlistPosition: data.waitlistPosition || undefined,
        }));
      } else if (response.status === 403) {
        setAuthState(prev => ({
          ...prev,
          user,
          loading: false,
          emailVerified: !!user.emailVerified,
          isApproved: false,
          isAdmin: false,
        }));
      } else {
        console.error('❌ Failed to check approval status:', response.status, response.statusText);
        undecided(`API connection failed: HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error checking approval status:', error);
      undecided(null);
    }
  }, []);

  useEffect(() => () => clearTimeout(retry.current), []);

  useEffect(() => {
    const unsubscribe = subscribeToAuthState(async (user) => {
      if (user) {
        await syncApproval(user);
      } else {
        clearTimeout(retry.current);
        setAuthState(prev => ({
          ...prev,
          user: null,
          loading: false,
          isApproved: false,
          isAdmin: false,
          emailVerified: false,
        }));
      }
    });

    return unsubscribe;
  }, [syncApproval]);

  const signIn = async () => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      const user = await signInWithGoogle();
      const token = await getCurrentUserToken();
      if (token) console.log('✅ User signed in successfully:', user.email);
      setAuthState(prev => ({ ...prev, loading: false }));
      return user;
    } catch (error: any) {
      console.error('❌ Sign in failed:', error);
      setAuthState(prev => ({ ...prev, loading: false, error: error.message || 'Sign in failed' }));
      throw error;
    }
  };

  const signInEmail = async (email: string, password: string) => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      const user = await signInWithEmailPassword(email, password);
      setAuthState(prev => ({ ...prev, loading: false }));
      return user;
    } catch (error: any) {
      console.error('❌ Email sign in failed:', error);
      setAuthState(prev => ({ ...prev, loading: false, error: error.message || 'Sign in failed' }));
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      // Creates the account (unverified) + sends the verification email. The
      // auth-state listener then registers the user with the backend.
      const user = await signUpWithEmailPassword(email, password);
      setAuthState(prev => ({ ...prev, loading: false }));
      return user;
    } catch (error: any) {
      console.error('❌ Sign up failed:', error);
      setAuthState(prev => ({ ...prev, loading: false, error: error.message || 'Sign up failed' }));
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    setAuthState(prev => ({ ...prev, error: null }));
    await sendPasswordReset(email);
  };

  const resendVerification = async () => {
    await resendVerificationEmail();
  };

  // Re-check verification after the user clicks the emailed link. Forces a token
  // refresh so the backend sees email_verified=true (and fires the welcome),
  // then re-syncs approval. Returns whether the email is now verified.
  const refreshVerification = async (): Promise<boolean> => {
    const verified = await refreshEmailVerified();
    const current = auth?.currentUser;
    if (current) await syncApproval(current);
    return verified;
  };

  const signOut = async () => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      await signOutUser();

      // Purge the previous account's cached projects/team so the next login
      // never hydrates them (the persisted stores + react-query snapshot would
      // otherwise survive the logout). Drop the uid marker too.
      resetUserScopedClientState();
      try { localStorage.removeItem(LAST_AUTH_UID_KEY); } catch { /* ignore */ }
      // Not in resetUserScopedClientState: App runs that on a uid change, which would wipe a new account's draft.
      if (frontDoorOn()) void clearDraft();

      console.log('✅ User signed out successfully');
      setAuthState(prev => ({ ...prev, user: null, loading: false, emailVerified: false }));
    } catch (error: any) {
      console.error('❌ Sign out failed:', error);
      setAuthState(prev => ({ ...prev, loading: false, error: error.message || 'Sign out failed' }));
    }
  };

  const clearError = () => {
    setAuthState(prev => ({ ...prev, error: null }));
  };

  return {
    user: authState.user,
    loading: authState.loading,
    error: authState.error,
    signIn,
    signInEmail,
    signUp,
    resetPassword,
    resendVerification,
    refreshVerification,
    signOut,
    clearError,
    isAuthenticated: !!authState.user,
    isApproved: authState.isApproved,
    isAdmin: authState.isAdmin,
    emailVerified: authState.emailVerified,
    waitlistPosition: authState.waitlistPosition,
  };
};
